import { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  createRole,
  deleteRole,
  listRoles,
  RoleManagement,
  updateRole,
} from '../../services/accessControlApi';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { TableActionLink } from '../../components/ui/TableActionLink';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';

export function RolesAdminPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('access-control.roles');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const [roles, setRoles] = useState<RoleManagement[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ code: '', name: '', description: '' });

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listRoles({ page: targetPage, pageSize });
      setRoles(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar perfis de acesso.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm({ code: '', name: '', description: '' });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    try {
      if (editingId) {
        await updateRole(editingId, form);
        toast.success('Perfil de acesso atualizado.');
        collapseForm(resetForm);
      } else {
        await createRole(form);
        toast.success('Perfil de acesso criado.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar perfil de acesso.');
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    if (
      !(await confirm({
        title: 'Excluir perfil de acesso',
        message: 'Excluir este perfil de acesso?',
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteRole(id);
      toast.success('Perfil de acesso excluído.');
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir perfil de acesso.');
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageShell>
      <PageHeader
        title="Perfis de acesso"
        description="Defina o que cada perfil pode fazer no sistema. Cargos organizacionais são gerenciados separadamente."
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar perfil de acesso' : 'Novo perfil de acesso'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'perfil'} — o formulário abriu em uma janela` : undefined
          }
          createSummary="Clique para cadastrar um novo perfil de acesso"
          isEditing={!!editingId}
        >
        <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
          <div className="erp-form-grid md:grid-cols-3">
            <input className="erp-input-compact" placeholder="Código" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
            <input className="erp-input-compact" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className="erp-input-compact" placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <FormActionBar onCancel={() => collapseForm(resetForm)} cancelLabel="Cancelar" className="erp-btn-actions mt-4">
            <button type="submit" className="erp-btn-primary">Salvar</button>
          </FormActionBar>
        </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="erp-table-wrap">
        {loading ? <p className="erp-loading">Carregando...</p> : (
          <div className="erp-table-scroll">
          <table className="erp-table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>{role.code}</td>
                  <td>{role.name}</td>
                  <td>{role.type === 'system' ? 'Sistema' : 'Customizado'}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <TableActionLink to={`/admin/roles/${role.id}/permissions`}>Permissões</TableActionLink>
                      {canWrite && role.type !== 'system' ? (
                        <>
                          <EditIconButton
                            label="Editar perfil de acesso"
                            onClick={() => {
                              expandForm();
                              setEditingId(role.id);
                              setForm({ code: role.code, name: role.name, description: role.description ?? '' });
                            }}
                          />
                          <DeleteIconButton label="Excluir perfil de acesso" onClick={() => void handleDelete(role.id)} />
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
